import {TeamStatCalculationResult, TeamStatType} from "./definition";
import {
	EventFilterCondition,
	GamePageLazyResponse,
	GamePageResponse,
	GPEventResponse,
	GPGamePlayerResponse,
	SecondBallAnalysisResult,
	EventTypes
} from "../../types";
import {createStatTypeConditions} from "../Video/Filter/conditionFactory";
import {createStatTypeFilterValues} from "../Video/Filter/filterFactory";
import EventFilter from "../Video/Filter/eventFilter";
import { excludeDirectionalEvents } from "../../functions";
import { ProcessingLevelEnum } from "../../types";

const filterEvents = (eventFilter: EventFilter, statType: TeamStatType, periods: string[], teamId: number | undefined = undefined): GPEventResponse[] => {
	const conditions = createStatTypeConditions(statType);

	if (conditions.length === 0) {
		return [];
	}

	conditions.push({ type: "period", value: periods})

	if (teamId) {
		conditions.push({ type: "team", value: [teamId.toString()] });
	}

	return eventFilter.filterByConditions(conditions, eventFilter.events);
}

const handleDivisionByZero = (
	numerator: number,
	denominator: number,
	multiplier = 1
): number => {
	if (denominator <= 0) return 0;
	return Math.round((numerator / denominator) * multiplier);
};

/**
 * Group duels by close timestamp
 */
const groupDuelsByTime = (events: GPEventResponse[], thresholdMs = 500) => {
	const sorted = [...events]
	.filter(hasFootballTime)
	.sort((a, b) => a.footballTime - b.footballTime);

	const groups = [];

	let currentGroup = [sorted[0]];
	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];

		if (curr.footballTime - prev.footballTime <= thresholdMs) {
			currentGroup.push(curr);
		} else {
			groups.push(currentGroup);
			currentGroup = [curr];
		}
	}
	if (currentGroup.length) {
		groups.push(currentGroup);
	}

	return groups;
}

const hasFootballTime = (e: GPEventResponse): e is GPEventResponse & { footballTime: number } => {
	return e.footballTime !== null;
}

const analyzeSecondBalls = (
	events: GPEventResponse[],
	teamId: number,
	periods?: string[]
): SecondBallAnalysisResult => {
	const aerialDuels = events.filter(
		(e: GPEventResponse) =>
			e.type === "aerial_duel" && (!periods || periods.includes(e.half?.toString() || ""))
	);

	if (aerialDuels.length === 0) {
		return {
			teamId,
			totalDuels: 0,
			secondBallsWon: 0,
			secondBallWinRate: 0,
		};
	}

	// Group duels by footballTime (within period of ms of each other)
	const groupedDuels: GPEventResponse[][] = groupDuelsByTime(aerialDuels, 2000);
	let totalDuels = 0;
	let secondBallsWon = 0;

	for (const duelGroup of groupedDuels) {
		const involved = duelGroup.filter(e => e.gameTeamId === teamId);

		// Team was in this duel
		if (involved.length > 0) {
			totalDuels++;
		}

		const duelTime = duelGroup[0].footballTime;
		const duelHalf = duelGroup[0].half;

		// Check next events after duel to see who gains possession
		const followUps = events.filter(
			(e: GPEventResponse) =>
				hasFootballTime(e) &&
				duelTime &&
				(!periods || periods.includes(e.half?.toString() || "")) &&
				e.footballTime > duelTime &&
				e.footballTime <= duelTime + 10000 &&
				e.half === duelHalf &&
				[EventTypes.Recovery, EventTypes.PassReceived, EventTypes.Interception].includes(e.type as EventTypes)
		);

		if (followUps.length > 0 && followUps[0].gameTeamId === teamId) {
			secondBallsWon++;
		}
	}

	return {
		teamId,
		totalDuels,
		secondBallsWon,
		secondBallWinRate: totalDuels > 0 ? (secondBallsWon / totalDuels) * 100 : 0,
	};
}

export function calculateTeamStats(eventFilter: EventFilter, gameData: GamePageResponse, statType: TeamStatType, periods: string[], gameDataLazy?: GamePageLazyResponse): TeamStatCalculationResult {
	const result = {
		isGoal: false,
		label: `gamePage.teamStats.type.${statType}`,
		description: `gamePage.teamStats.description.${statType}`,
		homeGameTeamId: gameData.homeGameTeam.id,
		awayGameTeamId: gameData.awayGameTeam.id,
		homeEvents: [],
		awayEvents: [],
		allEvents: [],
		homeValue: 0,
		homeValueFormatted: "0",
		awayValue: 0,
		awayValueFormatted: "0",
		allValue: 0,
		filterValues: [],
		isFilterable: false,
		isDescriptionOnly: false,
	} as TeamStatCalculationResult;

	const isLightTagged = gameData?.game?.processingLevel === ProcessingLevelEnum.Limited;

	switch (statType) {
		case TeamStatType.AverageRating:
			const activeHomePlayers = gameData.homeGameTeam.players.filter((p: GPGamePlayerResponse) => p.rating !== 0 && p.rating !== null);
			if (activeHomePlayers.length > 0) {
				result.homeValue = activeHomePlayers.reduce((averageRating, {rating}) => averageRating + (rating || 0), 0) / activeHomePlayers.length;
			}
			const activeAwayPlayers = gameData.awayGameTeam.players.filter((p: GPGamePlayerResponse) => p.rating !== 0 && p.rating !== null);
			if (activeAwayPlayers.length > 0) {
				result.awayValue = activeAwayPlayers.reduce((averageRating, {rating}) => averageRating + (rating || 0), 0) / activeAwayPlayers.length;
			}

			result.homeValue = Math.round(result.homeValue * 100) / 100;
			result.awayValue = Math.round(result.awayValue * 100) / 100;

			break;
		case TeamStatType.PassAccuracy:
			let allPassEvents = filterEvents(eventFilter, TeamStatType.Pass, periods);

			if (isLightTagged) {
				allPassEvents = excludeDirectionalEvents(allPassEvents);
			}

			result.allEvents = allPassEvents;
			result.homeEvents = result.allEvents.filter((e) => e.gameTeamId === gameData.homeGameTeam.id);
			result.awayEvents = result.allEvents.filter((e) => e.gameTeamId === gameData.awayGameTeam.id);

			if (result.homeEvents.length > 0) {
				result.homeValue = result.homeEvents.reduce((successPasses, {success}) => success === true ? successPasses + 1 : successPasses, 0) / result.homeEvents.length;
				result.homeValue = Math.round(result.homeValue * 100);
			}

			if (result.awayEvents.length > 0) {
				result.awayValue = result.awayEvents.reduce((successPasses, {success}) => success === true ? successPasses + 1 : successPasses, 0) / result.awayEvents.length;
				result.awayValue = Math.round(result.awayValue * 100);
			}

			break;
		case TeamStatType.Possession:
			let sourceEvents = eventFilter.filterByConditions([{ type: "period", value: periods }] as EventFilterCondition[], gameDataLazy?.events || []);

			if (isLightTagged) {
				sourceEvents = excludeDirectionalEvents(sourceEvents);
			}

			const homePossessions = sourceEvents.filter((e)=>
				e.gameTeamId === gameData.homeGameTeam.id
				&& e.category !== "administrative"
				&& !["foul", "foul_committed", "goal_conceded"].includes(e.type)
			).length;
			const awayPossessions = sourceEvents.filter((e)=>
				e.gameTeamId === gameData.awayGameTeam.id
				&& e.category !== "administrative"
				&& !["foul", "foul_committed", "goal_conceded"].includes(e.type)
			).length;

			const totalPossessions = homePossessions + awayPossessions;

			if (totalPossessions !== 0) {
				result.homeValue = Math.round((homePossessions / totalPossessions) * 100);
				result.awayValue = Math.round((awayPossessions / totalPossessions) * 100);
			}

			break;
		case TeamStatType.Goal:
			result.homeGoalEvents = filterEvents(eventFilter, TeamStatType.Goal, periods, gameData.homeGameTeam.id);
			result.awayGoalEvents = filterEvents(eventFilter, TeamStatType.Goal, periods, gameData.awayGameTeam.id);
			result.homeOwnGoalEvents = filterEvents(eventFilter, TeamStatType.GoalOwn, periods, gameData.homeGameTeam.id);
			result.awayOwnGoalEvents = filterEvents(eventFilter, TeamStatType.GoalOwn, periods, gameData.awayGameTeam.id);
			if (isLightTagged) {
				result.homeOwnGoalEvents = excludeDirectionalEvents(result.homeOwnGoalEvents || []);
				result.homeGoalEvents = excludeDirectionalEvents(result.homeGoalEvents || []);
				result.awayGoalEvents = excludeDirectionalEvents(result.awayGoalEvents || []);
				result.awayOwnGoalEvents = excludeDirectionalEvents(result.awayOwnGoalEvents || []);
			}
			result.homeValue = result.homeGoalEvents.length + result.awayOwnGoalEvents.length;
			result.awayValue = result.awayGoalEvents.length + result.homeOwnGoalEvents.length;
			result.isFilterable = (result.homeValue + result.awayValue) !== 0;
			result.goalFilterValues = createStatTypeFilterValues(TeamStatType.Goal);
			result.ownGoalFilterValues = createStatTypeFilterValues(TeamStatType.GoalOwn);
			result.filterValues = [
				...createStatTypeFilterValues(TeamStatType.Goal),
				...createStatTypeFilterValues(TeamStatType.GoalOwn),
			];

			// don't show like goals if now own goals presented
			result.isGoal = result.homeOwnGoalEvents.length > 0 || result.awayOwnGoalEvents.length > 0;

			break;
		case TeamStatType.SecondBallWon:
			const eventsForSecondBalls = isLightTagged ? excludeDirectionalEvents(eventFilter.events) : eventFilter.events;
			const homeSecondBalls = analyzeSecondBalls(eventsForSecondBalls, gameData.homeGameTeam.id, periods);
			const awaySecondBalls = analyzeSecondBalls(eventsForSecondBalls, gameData.awayGameTeam.id, periods);

			// Ensure percentages always equal 100%
			const totalRate = homeSecondBalls.secondBallWinRate + awaySecondBalls.secondBallWinRate;
			if (totalRate > 0) {
				let homeRate = Math.round((homeSecondBalls.secondBallWinRate / totalRate) * 100);
				let awayRate = Math.round((awaySecondBalls.secondBallWinRate / totalRate) * 100);

				homeSecondBalls.secondBallWinRate = homeRate;
				awaySecondBalls.secondBallWinRate = awayRate;
			} else {
				homeSecondBalls.secondBallWinRate = 0;
				awaySecondBalls.secondBallWinRate = 0;
			}

			result.homeValue = parseInt(homeSecondBalls.secondBallWinRate.toString());
			result.awayValue = parseInt(awaySecondBalls.secondBallWinRate.toString());
			result.isFilterable = (result.homeValue + result.awayValue) !== 0;
			result.isDescriptionOnly = true;

			break;
		case TeamStatType.FieldTilt:
			let homeAllThirdPass = filterEvents(eventFilter, TeamStatType.PassInFinalThird, periods, gameData.homeGameTeam.id);
			let awayAllThirdPass = filterEvents(eventFilter, TeamStatType.PassInFinalThird, periods, gameData.awayGameTeam.id);

			if (isLightTagged) {
				homeAllThirdPass = excludeDirectionalEvents(homeAllThirdPass);
				awayAllThirdPass = excludeDirectionalEvents(awayAllThirdPass);
			}

			const sumPasses = homeAllThirdPass.length + awayAllThirdPass.length;
			const homeFieldTilt = sumPasses > 0 ? Math.round((homeAllThirdPass.length / sumPasses) * 100) : 0;
			const awayFieldTilt = sumPasses > 0 ? Math.round((awayAllThirdPass.length / sumPasses) * 100) : 0;

			result.homeValue = homeFieldTilt;
			result.awayValue = awayFieldTilt;

			result.isFilterable = (result.homeValue + result.awayValue) !== 0;
			result.isDescriptionOnly = true;

			break;
		case TeamStatType.ShotConversion:
			let homeShots = filterEvents(eventFilter, TeamStatType.Shot, periods, gameData.homeGameTeam.id);
			let awayShots = filterEvents(eventFilter, TeamStatType.Shot, periods, gameData.awayGameTeam.id);
			let homeGoals = filterEvents(eventFilter, TeamStatType.Goal, periods, gameData.homeGameTeam.id);
			let awayGoals = filterEvents(eventFilter, TeamStatType.Goal, periods, gameData.awayGameTeam.id);

			if (isLightTagged) {
				homeShots = excludeDirectionalEvents(homeShots);
				awayShots = excludeDirectionalEvents(awayShots);
				homeGoals = excludeDirectionalEvents(homeGoals);
				awayGoals = excludeDirectionalEvents(awayGoals);
			}

			const homeShotConversion = handleDivisionByZero(homeGoals.length, homeShots.length, 100);
			const awayShotConversion = handleDivisionByZero(awayGoals.length, awayShots.length, 100);

			result.homeValue = homeShotConversion;
			result.awayValue = awayShotConversion;

			result.isFilterable = (result.homeValue + result.awayValue) !== 0;
			result.isDescriptionOnly = true;

			break;
		default:
			result.homeValue = !isLightTagged
				? filterEvents(eventFilter, statType, periods, gameData.homeGameTeam.id).length
				: excludeDirectionalEvents(filterEvents(eventFilter, statType, periods, gameData.homeGameTeam.id)).length;
			result.awayValue = !isLightTagged
				? filterEvents(eventFilter, statType, periods, gameData.awayGameTeam.id).length
				: excludeDirectionalEvents(filterEvents(eventFilter, statType, periods, gameData.awayGameTeam.id)).length;
			result.isFilterable = (result.homeValue + result.awayValue) !== 0;

			break;
	}

	result.allValue = result.homeValue + result.awayValue;

	switch (statType) {
		case TeamStatType.Possession:
		case TeamStatType.PassAccuracy:
		case TeamStatType.SecondBallWon:
		case TeamStatType.ShotConversion:
		case TeamStatType.FieldTilt:
			result.homeValueFormatted = `${result.homeValue}%`;
			result.awayValueFormatted = `${result.awayValue}%`;

			break;
		default:
			result.homeValueFormatted = String(result.homeValue);
			result.awayValueFormatted = String(result.awayValue);

			break;
	}

	if (!result.isGoal) {
		result.filterValues = createStatTypeFilterValues(statType);
	}

	result.isFilterable = result.isFilterable && result.filterValues.length > 0;

	periods.forEach((p) => {
		result.filterValues.push({
			key: `period.${p}`,
			value: [p],
		});
	});

	return result;
}
